import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { invalidateJobTitle } from '@/lib/jobTitle';
import { useUi } from '@/lib/store';
import { usePermissions } from '@/lib/permissions';
import { Button, Input } from '@/components/ui';
import { jobTitleEditable, type PersonProfile } from '@tradeoutlet/shared';

type JobTitlePerson = Pick<PersonProfile, 'user_id' | 'job_title'> & Partial<Pick<PersonProfile, 'can' | 'buildings' | 'teams'>>;

/**
 * A person's job title (t128) on the drawer, the phone person sheet and My account. t441u: who may change it is the
 * shared rule (jobTitleEditable, as the API enforces it). They get a pencil and edit it inline; everyone else sees
 * the title, or a muted "No job title", as plain text.
 */
export function JobTitleLine({ ws, person, className }: { ws: string; person: JobTitlePerson; className?: string }) {
  const mine = usePermissions().data;
  const canEdit = jobTitleEditable(person, mine);
  const { user_id: userId, job_title: title } = person;
  const toast = useUi(s => s.toast);
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (draft === null) return;
    setBusy(true);
    try {
      await api.wf.setJobTitle(ws, userId, draft.trim() || null);
      invalidateJobTitle(ws, userId);
      setDraft(null);
      toast('Job title saved', 'success');
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  if (draft !== null) {
    return (
      <form className="mt-0.5 flex items-center gap-1" onSubmit={e => { e.preventDefault(); void save(); }}>
        <Input autoFocus aria-label="Job title" data-testid="job-title-input" maxLength={120} value={draft} className="h-7 py-0 text-sm"
          onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setDraft(null); } }} />
        <Button type="submit" size="icon" variant="ghost" aria-label="Save job title" title="Save" disabled={busy} data-testid="job-title-save"><Check className="h-4 w-4" /></Button>
        <Button type="button" size="icon" variant="ghost" aria-label="Cancel" title="Cancel" onClick={() => setDraft(null)}><X className="h-4 w-4" /></Button>
      </form>
    );
  }
  return (
    <div className={`group flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300 ${className ?? ''}`}>
      <span data-testid="person-job-title">{title ?? <span className="text-slate-400">No job title</span>}</span>
      {canEdit && (
        <button type="button" data-testid="job-title-edit" aria-label="Edit job title" title="Edit job title" onClick={() => setDraft(title ?? '')}
          className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-2 dark:hover:bg-slate-800 dark:hover:text-slate-100">
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
